import React, { useState } from 'react';
import { Card, Form, Row, Col, Table } from 'react-bootstrap';
import Content from "../../../Components/Dashboard/Content/Content";

export default function SimpleCalculation() {
    const [data, setData] = useState({
        entryPrice: '',
        exitPrice: '',
        quantity: '',
        leverage: '1'
    });

    const USD_TO_INR = 83.50; // Current USD to INR rate
    const BROKERAGE_RATE = 0.05; // 0.05% per trade
    const EXCHANGE_TXN_RATE = 0.002; // 0.002% exchange charge

    // Handle positive numbers only
    const handlePositiveNumber = (value, maxDecimals = null) => {
        if (value === '') return '';
        if (Number(value) < 0) return '';
        
        if (maxDecimals !== null && value.includes('.')) {
            const [int, dec] = value.split('.');
            return int + '.' + dec.slice(0, maxDecimals);
        }
        
        return value;
    };

    // Calculate all metrics
    const calculateMetrics = () => {
        const { entryPrice, exitPrice, quantity, leverage } = data;

        if (!entryPrice || !exitPrice || !quantity || !leverage) {
            return {
                grossPnL_USD: 0,
                grossPnL_INR: 0,
                entryBrokerage_USD: 0,
                entryBrokerage_INR: 0,
                exitBrokerage_USD: 0,
                exitBrokerage_INR: 0,
                totalBrokerage_USD: 0,
                totalBrokerage_INR: 0,
                exchangeTxn_USD: 0,
                exchangeTxn_INR: 0,
                totalCharges_USD: 0,
                totalCharges_INR: 0,
                netPnL_USD: 0,
                netPnL_INR: 0,
                buyValue_USD: 0,
                buyValue_INR: 0,
                sellValue_USD: 0,
                sellValue_INR: 0,
                turnover_USD: 0,
                turnover_INR: 0,
                requiredFund_USD: 0,
                requiredFund_INR: 0,
                totalPosition_USD: 0,
                totalPosition_INR: 0
            };
        }

        const entry = parseFloat(entryPrice);
        const exit = parseFloat(exitPrice);
        const qty = parseFloat(quantity);
        const lev = parseFloat(leverage);

        // Calculate position values
        const totalPosition_USD = entry * qty;
        const totalPosition_INR = totalPosition_USD * USD_TO_INR;

        // Required fund (with leverage)
        const requiredFund_USD = totalPosition_USD / lev;
        const requiredFund_INR = requiredFund_USD * USD_TO_INR;

        // Gross P&L
        const grossPnL_USD = (exit - entry) * qty;
        const grossPnL_INR = grossPnL_USD * USD_TO_INR;

        // Buy and Sell values
        const buyValue_USD = entry * qty;
        const buyValue_INR = buyValue_USD * USD_TO_INR;
        const sellValue_USD = exit * qty;
        const sellValue_INR = sellValue_USD * USD_TO_INR;

        // Turnover
        const turnover_USD = buyValue_USD + sellValue_USD;
        const turnover_INR = turnover_USD * USD_TO_INR;

        // Brokerage
        const entryBrokerage_USD = (buyValue_USD * BROKERAGE_RATE) / 100;
        const entryBrokerage_INR = entryBrokerage_USD * USD_TO_INR;
        const exitBrokerage_USD = (sellValue_USD * BROKERAGE_RATE) / 100;
        const exitBrokerage_INR = exitBrokerage_USD * USD_TO_INR;
        const totalBrokerage_USD = entryBrokerage_USD + exitBrokerage_USD;
        const totalBrokerage_INR = totalBrokerage_USD * USD_TO_INR;

        // Exchange charges
        const exchangeTxn_USD = (turnover_USD * EXCHANGE_TXN_RATE) / 100;
        const exchangeTxn_INR = exchangeTxn_USD * USD_TO_INR;

        // Total charges
        const totalCharges_USD = totalBrokerage_USD + exchangeTxn_USD;
        const totalCharges_INR = totalCharges_USD * USD_TO_INR;

        // Net P&L
        const netPnL_USD = grossPnL_USD - totalCharges_USD;
        const netPnL_INR = netPnL_USD * USD_TO_INR;

        return {
            grossPnL_USD: grossPnL_USD.toFixed(2),
            grossPnL_INR: grossPnL_INR.toFixed(2),
            entryBrokerage_USD: entryBrokerage_USD.toFixed(2),
            entryBrokerage_INR: entryBrokerage_INR.toFixed(2),
            exitBrokerage_USD: exitBrokerage_USD.toFixed(2),
            exitBrokerage_INR: exitBrokerage_INR.toFixed(2),
            totalBrokerage_USD: totalBrokerage_USD.toFixed(2),
            totalBrokerage_INR: totalBrokerage_INR.toFixed(2),
            exchangeTxn_USD: exchangeTxn_USD.toFixed(2),
            exchangeTxn_INR: exchangeTxn_INR.toFixed(2),
            totalCharges_USD: totalCharges_USD.toFixed(2),
            totalCharges_INR: totalCharges_INR.toFixed(2),
            netPnL_USD: netPnL_USD.toFixed(2),
            netPnL_INR: netPnL_INR.toFixed(2),
            buyValue_USD: buyValue_USD.toFixed(2),
            buyValue_INR: buyValue_INR.toFixed(2),
            sellValue_USD: sellValue_USD.toFixed(2),
            sellValue_INR: sellValue_INR.toFixed(2),
            turnover_USD: turnover_USD.toFixed(2),
            turnover_INR: turnover_INR.toFixed(2),
            requiredFund_USD: requiredFund_USD.toFixed(2),
            requiredFund_INR: requiredFund_INR.toFixed(2),
            totalPosition_USD: totalPosition_USD.toFixed(2),
            totalPosition_INR: totalPosition_INR.toFixed(2)
        };
    };

    const metrics = calculateMetrics();

    return (
        <Content
            button_status={false}
            Page_title="Crypto P&L Calculator"
            Page_title_showClient="Calculate your profit/loss with leverage"
        >
            <div className="calculation-container">
                <Row className="g-4">
                    {/* Input Card */}
                    <Col lg={5}>
                        <Card className="shadow-sm h-100">
                            <Card.Header className="bg-primary text-white">
                                <h5 className="mb-0">📊 Trade Details</h5>
                            </Card.Header>
                            <Card.Body className="p-4">
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">Entry Price (USD)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        placeholder="Enter entry price"
                                        value={data.entryPrice}
                                        onChange={(e) => setData({...data, entryPrice: handlePositiveNumber(e.target.value, 2)})}
                                        className="py-2"
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">Exit Price (USD)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        placeholder="Enter exit price"
                                        value={data.exitPrice}
                                        onChange={(e) => setData({...data, exitPrice: handlePositiveNumber(e.target.value, 2)})}
                                        className="py-2"
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">Quantity</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        step="0.001"
                                        placeholder="Enter quantity"
                                        value={data.quantity}
                                        onChange={(e) => setData({...data, quantity: handlePositiveNumber(e.target.value, 3)})}
                                        className="py-2"
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">Leverage (x)</Form.Label>
                                    <Form.Select
                                        value={data.leverage}
                                        onChange={(e) => setData({...data, leverage: e.target.value})}
                                        className="py-2"
                                    >
                                        <option value="1">1x (No Leverage)</option>
                                        <option value="2">2x</option>
                                        <option value="3">3x</option>
                                        <option value="5">5x</option>
                                        <option value="10">10x</option>
                                        <option value="20">20x</option>
                                        <option value="50">50x</option>
                                        <option value="100">100x</option>
                                    </Form.Select>
                                </Form.Group>

                                {/* Fund Required Alert */}
                                <div className="alert alert-info mt-4">
                                    <h6 className="fw-bold mb-3">💰 Required Fund</h6>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span>In USD:</span>
                                        <strong className="text-primary">${metrics.requiredFund_USD}</strong>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span>In INR:</span>
                                        <strong className="text-primary">₹{metrics.requiredFund_INR}</strong>
                                    </div>
                                    <hr />
                                    <small className="text-muted">
                                        Total Position: ${metrics.totalPosition_USD} (₹{metrics.totalPosition_INR})
                                    </small>
                                </div>

                                {/* Rates Info */}
                                <div className="alert alert-light">
                                    <small>
                                        <div className="d-flex justify-content-between mb-1">
                                            <span>Brokerage Rate:</span>
                                            <strong>{BROKERAGE_RATE}% per trade</strong>
                                        </div>
                                        <div className="d-flex justify-content-between mb-1">
                                            <span>Exchange Charge:</span>
                                            <strong>{EXCHANGE_TXN_RATE}%</strong>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <span>USD to INR:</span>
                                            <strong>₹{USD_TO_INR}</strong>
                                        </div>
                                    </small>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Results Card */}
                    <Col lg={7}>
                        <Card className="shadow-sm h-100">
                            <Card.Header className="bg-primary text-white">
                                <h5 className="mb-0">💹 P&L Breakdown</h5>
                            </Card.Header>
                            <Card.Body className="p-4">
                                {/* Gross P&L */}
                                <div className={`alert ${parseFloat(metrics.grossPnL_USD) >= 0 ? 'alert-success' : 'alert-danger'} mb-4`}>
                                    <h6 className="mb-2">Gross P&L</h6>
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <span>USD:</span>
                                        <h4 className="mb-0 fw-bold">
                                            {parseFloat(metrics.grossPnL_USD) >= 0 ? '+' : ''}${metrics.grossPnL_USD}
                                        </h4>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span>INR:</span>
                                        <h5 className="mb-0 fw-bold">
                                            {parseFloat(metrics.grossPnL_INR) >= 0 ? '+' : ''}₹{metrics.grossPnL_INR}
                                        </h5>
                                    </div>
                                </div>

                                {/* Charges Breakdown */}
                                <Table bordered hover size="sm" className="mb-4">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Description</th>
                                            <th className="text-end">USD ($)</th>
                                            <th className="text-end">INR (₹)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="text-muted">Buy Value</td>
                                            <td className="text-end fw-semibold">${metrics.buyValue_USD}</td>
                                            <td className="text-end fw-semibold">₹{metrics.buyValue_INR}</td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">Sell Value</td>
                                            <td className="text-end fw-semibold">${metrics.sellValue_USD}</td>
                                            <td className="text-end fw-semibold">₹{metrics.sellValue_INR}</td>
                                        </tr>
                                        <tr className="table-light">
                                            <td className="fw-semibold">Total Turnover</td>
                                            <td className="text-end fw-bold">${metrics.turnover_USD}</td>
                                            <td className="text-end fw-bold">₹{metrics.turnover_INR}</td>
                                        </tr>
                                        <tr className="table-warning">
                                            <td colSpan="3" className="fw-bold">Charges Breakdown</td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted ps-3">Entry Brokerage ({BROKERAGE_RATE}%)</td>
                                            <td className="text-end text-danger">-${metrics.entryBrokerage_USD}</td>
                                            <td className="text-end text-danger">-₹{metrics.entryBrokerage_INR}</td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted ps-3">Exit Brokerage ({BROKERAGE_RATE}%)</td>
                                            <td className="text-end text-danger">-${metrics.exitBrokerage_USD}</td>
                                            <td className="text-end text-danger">-₹{metrics.exitBrokerage_INR}</td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted ps-3">Exchange Charges ({EXCHANGE_TXN_RATE}%)</td>
                                            <td className="text-end text-danger">-${metrics.exchangeTxn_USD}</td>
                                            <td className="text-end text-danger">-₹{metrics.exchangeTxn_INR}</td>
                                        </tr>
                                        <tr className="table-warning">
                                            <td className="fw-semibold">Total Charges</td>
                                            <td className="text-end fw-bold text-danger">-${metrics.totalCharges_USD}</td>
                                            <td className="text-end fw-bold text-danger">-₹{metrics.totalCharges_INR}</td>
                                        </tr>
                                    </tbody>
                                </Table>

                                {/* Net P&L */}
                                <div className={`alert ${parseFloat(metrics.netPnL_USD) >= 0 ? 'alert-success' : 'alert-danger'}`}>
                                    <h5 className="mb-2 fw-bold">Net P&L (After All Charges)</h5>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="fs-5">USD:</span>
                                        <h3 className="mb-0 fw-bold">
                                            {parseFloat(metrics.netPnL_USD) >= 0 ? '+' : ''}${metrics.netPnL_USD}
                                        </h3>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="fs-5">INR:</span>
                                        <h4 className="mb-0 fw-bold">
                                            {parseFloat(metrics.netPnL_INR) >= 0 ? '+' : ''}₹{metrics.netPnL_INR}
                                        </h4>
                                    </div>
                                </div>

                                {/* Example Note */}
                                <div className="mt-3 p-3 bg-light rounded">
                                    <small className="text-muted">
                                        <strong>Example:</strong> BTC @ $80,000 entry and $90,000 exit with 0.1 BTC quantity.
                                        <br/>With 10x leverage, you only need ${metrics.requiredFund_USD || '800'} instead of ${metrics.totalPosition_USD || '8,000'}.
                                    </small>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>

            <style jsx>{`
                .calculation-container {
                    max-width: 1400px;
                    margin: 0 auto;
                }
                
                .card {
                    border: none;
                    transition: transform 0.2s ease;
                }
                
                .card:hover {
                    transform: translateY(-2px);
                }
                
                .form-control:focus, .form-select:focus {
                    border-color: #0d6efd;
                    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
                }
                
                .alert {
                    border-radius: 10px;
                }
                
                .table td, .table th {
                    vertical-align: middle;
                }
            `}</style>
        </Content>
    );
}